import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import RoomIcon from '@mui/icons-material/Room';
import { BookingSession } from '../../types';
import {
  DEFAULT_LEAFLET_CENTER,
  ensureLeafletAssets,
} from '@/utils/leafletLoader';

type SessionsMapProps = {
  sessions: BookingSession[];
  selectedSessionId?: number | null;
  onSelectSession?: (sessionId: number) => void;
  isLoading?: boolean;
};

const parseCoordinate = (value?: string | number | null) => {
  const numeric =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
      ? parseFloat(value)
      : NaN;
  return Number.isFinite(numeric) ? numeric : null;
};

const markerStyle = (isSelected: boolean) => ({
  radius: isSelected ? 11 : 7,
  color: isSelected ? '#0d6efd' : alpha('#0f172a', 0.7),
  fillColor: isSelected ? '#22c55e' : '#38bdf8',
  fillOpacity: isSelected ? 0.9 : 0.75,
  weight: isSelected ? 2 : 1,
});

export default function SessionsMap({
  sessions,
  selectedSessionId,
  onSelectSession,
  isLoading,
}: SessionsMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<Record<number, any>>({});
  const [isLeafletReady, setIsLeafletReady] = useState(false);

  useEffect(() => {
    ensureLeafletAssets()
      .then(() => setIsLeafletReady(true))
      .catch(() => setIsLeafletReady(false));
  }, []);

  useEffect(() => {
    if (!isLeafletReady || mapRef.current || !containerRef.current) return;
    if (!window.L) return;

    const mapInstance = window.L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: false,
    }).setView(DEFAULT_LEAFLET_CENTER, 6);

    window.L
      .tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      })
      .addTo(mapInstance);

    mapRef.current = mapInstance;

    return () => {
      mapInstance.remove();
      mapRef.current = null;
      markersRef.current = {};
    };
  }, [isLeafletReady]);

  const sessionLocations = useMemo(() => {
    return sessions
      .map((session) => {
        const lat = parseCoordinate(session.address?.latitude);
        const lng = parseCoordinate(session.address?.longitude);
        if (lat === null || lng === null) return null;
        return { session, lat, lng };
      })
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
  }, [sessions]);

  useEffect(() => {
    if (!mapRef.current || !window.L) return;

    Object.values(markersRef.current).forEach((marker) => {
      marker.remove();
    });
    markersRef.current = {};

    sessionLocations.forEach(({ session, lat, lng }) => {
      const marker = window.L
        .circleMarker([lat, lng], markerStyle(false))
        .addTo(mapRef.current);

      marker.on('click', () => onSelectSession?.(session.id));
      marker.bindTooltip(
        `#${session.id} • ${session.address?.formatted_address ?? ''}`,
        { direction: 'top', offset: [0, -8] }
      );

      markersRef.current[session.id] = marker;
    });

    if (sessionLocations.length) {
      const bounds = window.L.latLngBounds(
        sessionLocations.map((loc) => [loc.lat, loc.lng])
      );
      mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    } else {
      mapRef.current.setView(DEFAULT_LEAFLET_CENTER, 6);
    }
  }, [sessionLocations, onSelectSession]);

  useEffect(() => {
    if (!mapRef.current || !window.L) return;
    Object.entries(markersRef.current).forEach(([id, marker]) => {
      const isSelected = Number(id) === selectedSessionId;
      if (marker?.setStyle) {
        marker.setStyle(markerStyle(isSelected));
        if (isSelected && marker.bringToFront) {
          marker.bringToFront();
        }
      }
    });

    if (selectedSessionId && markersRef.current[selectedSessionId]) {
      const latLng = markersRef.current[selectedSessionId].getLatLng?.();
      if (latLng) {
        mapRef.current.panTo(latLng, { animate: true, duration: 0.5 });
      }
    }
  }, [selectedSessionId]);

  return (
    <Box>
      <Stack direction="row" mb={1} alignItems="center" spacing={1}>
        <RoomIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={600}>
          Today&apos;s sessions map
        </Typography>
      </Stack>
      <Box
        sx={{
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          minHeight: 320,
          bgcolor: 'background.neutral',
        }}
      >
        {!isLeafletReady && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ position: 'absolute', inset: 0, zIndex: 2 }}
            spacing={1}
          >
            <CircularProgress size={28} />
            <Typography variant="caption" color="text.secondary">
              Loading map…
            </Typography>
          </Stack>
        )}

        {isLeafletReady && !sessionLocations.length && !isLoading && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ position: 'absolute', inset: 0, zIndex: 2, p: 3 }}
            spacing={1}
          >
            <Typography variant="body2" color="text.secondary" align="center">
              No geocoded sessions available for today. Add latitude and
              longitude to booking addresses to see them plotted here.
            </Typography>
          </Stack>
        )}

        <Box
          ref={containerRef}
          sx={{ height: 360, width: '100%', filter: isLeafletReady ? 'none' : 'blur(2px)' }}
        />
      </Box>
    </Box>
  );
}
